import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import Button from '../../components/ui/Button';
import Icon from '../../components/openplay/OpenPlayIcon';
import { supabase } from '../../lib/supabaseClient';

const OpenPlayParticipatedEventsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async (mountedRef) => {
      setLoading(true);
      setError('');

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (!userId) {
          setItems([]);
          setError('로그인이 필요합니다.');
          return;
        }

        const { data: participantRows, error: participantError } = await supabase
          .from('session_participants')
          .select('session_invite_id, status, applied_at, created_at')
          .eq('user_id', userId)
          .order('applied_at', { ascending: false })
          .limit(200);

        if (participantError) {
          throw new Error(
            `참여 이벤트 기록(session_participants) 조회에 실패했습니다: ${participantError?.message || '알 수 없는 오류'}`
          );
        }

        const inviteIds = (participantRows || [])
          .map((r) => String(r?.session_invite_id || '').trim())
          .filter(Boolean);

        if (inviteIds.length === 0) {
          if (mountedRef.current) setItems([]);
          return;
        }

        const { data: invites, error: inviteError } = await supabase
          .from('session_invites')
          .select('id, token, title, date, end_date, start_time, end_time, venue, venue_address, status, event_type, cover_image_thumb_url, cover_image_url')
          .in('id', inviteIds);

        if (inviteError) {
          throw new Error(inviteError?.message || '이벤트 정보를 불러오지 못했습니다.');
        }

        const inviteById = new Map((invites || []).map((it) => [String(it?.id), it]));

        const merged = (participantRows || [])
          .map((row) => {
            const invite = inviteById.get(String(row?.session_invite_id || ''));
            if (!invite) return null;
            return {
              participant: row,
              invite,
            };
          })
          .filter(Boolean);

        if (mountedRef.current) setItems(merged);
      } catch (e) {
        if (mountedRef.current) setError(e?.message || '불러오는 중 오류가 발생했습니다.');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    }, []);

  useEffect(() => {
    const mountedRef = { current: true };

    load(mountedRef);

    const handleFocus = () => {
      load(mountedRef);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        load(mountedRef);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [load, refreshTick]);

  const displayItems = useMemo(() => {
    return Array.isArray(items) ? items : [];
  }, [items]);

  return (
    <OpenPlayToolsLayout userRole="player">
      <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-6 px-4 max-w-6xl mx-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">참여 중 이벤트</h1>
            <p className="text-sm caption text-muted-foreground mt-2">내가 참여했거나 참여 중인 이벤트 기록을 확인할 수 있습니다.</p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setRefreshTick((v) => v + 1)}>
              새로고침
            </Button>
            <Button variant="outline" onClick={() => navigate('/openplay/tools')} iconName="ChevronLeft" iconPosition="left">
              도구
            </Button>
          </div>
        </div>

        {error && (
          <div className="mt-6 bg-error/10 border border-error/20 rounded-xl p-4">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        <div className="mt-6 md:mt-8">
          {loading ? (
            <div className="bg-card border border-border rounded-xl p-8 md:p-12 text-center">
              <p className="text-sm md:text-base caption text-muted-foreground">불러오는 중...</p>
            </div>
          ) : displayItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {displayItems.map(({ invite, participant }) => {
                const cover = invite?.cover_image_thumb_url || invite?.cover_image_url || '';
                const dateText = String(invite?.date || '').trim();
                const start = String(invite?.start_time || '').trim();
                const end = String(invite?.end_time || '').trim();
                const venue = String(invite?.venue || invite?.venue_address || '').trim();

                return (
                  <button
                    key={`${invite?.id}`}
                    type="button"
                    className="bg-card border border-border rounded-xl hover-lift transition-athletic overflow-hidden text-left"
                    onClick={() => {
                      const token = String(invite?.token || '').trim();
                      if (!token) return;
                      navigate(`/public-session-page?token=${encodeURIComponent(token)}`);
                    }}
                  >
                    <div className="p-5 md:p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {cover ? (
                            <img src={cover} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <Icon name="ClipboardDocumentListIcon" size={28} className="text-primary" />
                          )}
                        </div>

                        <div className="min-w-0">
                          <p className="text-base font-heading font-semibold text-foreground truncate">{invite?.title}</p>
                          <p className="text-xs caption text-muted-foreground mt-1">
                            {dateText}
                            {start ? ` ${start}` : ''}
                            {end ? ` ~ ${end}` : ''}
                          </p>
                          {venue && <p className="text-xs caption text-muted-foreground mt-1 truncate">{venue}</p>}
                          <p className="text-[11px] caption text-muted-foreground mt-2">신청 상태: {participant?.status || 'applied'}</p>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-8 md:p-12 text-center">
              <p className="text-base font-heading font-semibold text-foreground">참여한 이벤트가 없습니다</p>
              <p className="text-sm caption text-muted-foreground mt-2">이벤트에 참가 신청을 하면 여기에 기록이 표시됩니다.</p>
            </div>
          )}
        </div>
      </main>
    </OpenPlayToolsLayout>
  );
};

export default OpenPlayParticipatedEventsPage;
